import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "../components/PageHeader";
import createAutocompleteFilter from "pages/utils/autocompleteFilter";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";

// -------- filtros simples ----------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// -------- persistência de filtros ----------
const FILTERS_KEY = "tbl:acessosfuncoes:filters";
const KEEP_KEY = "tbl:acessosfuncoes:keep";

const autocompleteFilter = createAutocompleteFilter();

export default function AcessosFuncoes() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [data, setData] = useState([]);
  const [acessos, setAcessos] = useState([]);
  const [funcoes, setFuncoes] = useState([]);

  const initKeep = (() => {
    try {
      const v = localStorage.getItem(KEEP_KEY);
      return v === null ? true : JSON.parse(v);
    } catch {
      return true;
    }
  })();
  const [keepFilters, setKeepFilters] = useState(initKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  const carregar = async () => {
    const [rows, err] = await handleApi(api.post("AcessosFuncoes/SelAcessosFuncoes", {}));
    if (err) return showAlert(err, "error");
    setData(Array.isArray(rows) ? rows : []);
  };

  const carregarCombos = async () => {
    const [ac, errA] = await handleApi(api.post("AcessosFuncoes/SelAcessosCombo", {}));
    if (errA) return showAlert(errA, "error");
    const [fu, errF] = await handleApi(api.post("AcessosFuncoes/SelFuncoesCombo", {}));
    if (errF) return showAlert(errF, "error");
    setAcessos(Array.isArray(ac) ? ac : []);
    setFuncoes(Array.isArray(fu) ? fu : []);
  };

  useEffect(() => {
    (async () => {
      await carregar();
      await carregarCombos();
    })();
  }, []);

  // -------- Dialog Inserir --------
  const [openAdd, setOpenAdd] = useState(false);
  const [novo, setNovo] = useState({ idacesso: "", idfuncao: "", r: 1, w: 0, u: 0, d: 0 });

  const inserir = async () => {
    if (!novo.idacesso || !novo.idfuncao) {
      showAlert("Seleciona Acesso e Função.", "warning");
      return;
    }
    const body = {
      idacesso: Number(novo.idacesso),
      idfuncao: Number(novo.idfuncao),
      r: novo.r ? 1 : 0,
      w: novo.w ? 1 : 0,
      u: novo.u ? 1 : 0,
      d: novo.d ? 1 : 0,
    };
    const [_, err, meta] = await handleApi(api.post("AcessosFuncoes/InsAcessoFuncao", body));
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    setOpenAdd(false);
    setNovo({ idacesso: "", idfuncao: "", r: 1, w: 0, u: 0, d: 0 });
  };

  // -------- Dialog Editar (R/W/U/D apenas) --------
  const [openEdit, setOpenEdit] = useState(false);
  const [sel, setSel] = useState(null);

  const gravarEdicao = async () => {
    const [_, err, meta] = await handleApi(
      api.post("AcessosFuncoes/UpdAcessoFuncao", {
        id: sel.id,
        r: sel.r ? 1 : 0,
        w: sel.w ? 1 : 0,
        u: sel.u ? 1 : 0,
        d: sel.d ? 1 : 0,
      })
    );
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    setOpenEdit(false);
  };

  // -------- Confirmação (eliminar) --------
  const [openConfirm, setOpenConfirm] = useState(false);
  const [alvo, setAlvo] = useState(null);

  const pedirConfirmacao = (row) => {
    setAlvo(row);
    setOpenConfirm(true);
  };

  const fecharConfirmacao = () => {
    setOpenConfirm(false);
    setAlvo(null);
  };

  const confirmarEliminar = async () => {
    if (!alvo?.id) return fecharConfirmacao();
    const [_, err, meta] = await handleApi(
      api.post("AcessosFuncoes/DelAcessoFuncao", { id: alvo.id })
    );
    if (err) showAlert(err, "error");
    else if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    fecharConfirmacao();
  };

  // -------- Tabela --------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 88,
        minWidth: 72,
        maxWidth: 112,
        Cell: ({ row }) => {
          const a = row.original;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              <IconButton
                color="warning"
                size="small"
                disabled={!temPermissao("Menu Administração", "u")}
                onClick={() => {
                  setSel({ ...a });
                  setOpenEdit(true);
                }}
                title="Editar"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="error"
                size="small"
                disabled={!temPermissao("Menu Administração", "d")}
                onClick={() => pedirConfirmacao(a)}
                title="Eliminar"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      { Header: "Acesso", accessor: "acesso", Filter: TextFilter },
      { Header: "Função", accessor: "funcao", Filter: TextFilter },
      {
        Header: "Ver",
        accessor: "r",
        Filter: TextFilter,
        Cell: ({ value }) =>
          value === 1 ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />,
      },
      {
        Header: "Criar",
        accessor: "w",
        Filter: TextFilter,
        Cell: ({ value }) =>
          value === 1 ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />,
      },
      {
        Header: "Alterar",
        accessor: "u",
        Filter: TextFilter,
        Cell: ({ value }) =>
          value === 1 ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />,
      },
      {
        Header: "Apagar",
        accessor: "d",
        Filter: TextFilter,
        Cell: ({ value }) =>
          value === 1 ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />,
      },
    ],
    [temPermissao]
  );

  const table = useTable(
    {
      columns,
      data,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              {/* <MDTypography variant="h6">Acessos ↔ Funções</MDTypography> */}
              <MDBox display="flex" alignItems="center" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />
                <MDButton
                  color="success"
                  disabled={!temPermissao("Menu Administração", "w")}
                  onClick={() => setOpenAdd(true)}
                >
                  Nova Permissão
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            width: col.id === "acoes" ? col.width : undefined,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              padding: "8px 10px",
                              borderTop: "1px solid #eee",
                              fontSize: 12,
                              color: "#1a2b4c",
                              width: cell.column.id === "acoes" ? cell.column.width : undefined,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Inserir */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Permissão</DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Acesso</div>
              <Autocomplete
                size="small"
                options={acessos}
                filterOptions={autocompleteFilter}
                getOptionLabel={(option) => option.designacao || ""}
                value={acessos.find((a) => String(a.id) === String(novo.idacesso)) || null}
                onChange={(_, value) => setNovo((prev) => ({ ...prev, idacesso: value?.id || "" }))}
                renderInput={(params) => <TextField {...params} placeholder="Selecione…" />}
                disableClearable
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Função</div>
              <Autocomplete
                size="small"
                options={funcoes}
                filterOptions={autocompleteFilter}
                getOptionLabel={(option) => option.designacao || ""}
                value={funcoes.find((f) => String(f.id) === String(novo.idfuncao)) || null}
                onChange={(_, value) => setNovo((prev) => ({ ...prev, idfuncao: value?.id || "" }))}
                renderInput={(params) => <TextField {...params} placeholder="Selecione…" />}
                disableClearable
              />
            </div>

            <MDBox display="flex" gap={2} mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!novo.r}
                    onChange={(e) => setNovo((p) => ({ ...p, r: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="R (ler)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!novo.w}
                    onChange={(e) => setNovo((p) => ({ ...p, w: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="W (criar)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!novo.u}
                    onChange={(e) => setNovo((p) => ({ ...p, u: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="U (editar)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!novo.d}
                    onChange={(e) => setNovo((p) => ({ ...p, d: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="D (eliminar)"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAdd(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={inserir} color="success">
            Inserir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Editar */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Permissões</DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <MDInput label="Acesso" value={sel?.acesso || ""} disabled fullWidth />
            <MDInput label="Função" value={sel?.funcao || ""} disabled fullWidth />
            <MDBox display="flex" gap={2} mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!sel?.r}
                    onChange={(e) => setSel((p) => ({ ...p, r: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="R (ler)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!sel?.w}
                    onChange={(e) => setSel((p) => ({ ...p, w: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="W (criar)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!sel?.u}
                    onChange={(e) => setSel((p) => ({ ...p, u: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="U (editar)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!sel?.d}
                    onChange={(e) => setSel((p) => ({ ...p, d: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="D (eliminar)"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenEdit(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={gravarEdicao} color="success">
            Guardar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminação */}
      <Dialog open={openConfirm} onClose={fecharConfirmacao} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent>
          Tem a certeza que pretende eliminar a permissão
          {` `}
          <strong>{alvo ? `${alvo.acesso} — ${alvo.funcao}` : ""}</strong>?
        </DialogContent>
        <DialogActions>
          <MDButton onClick={fecharConfirmacao} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={confirmarEliminar} color="error">
            Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
